import { useEffect, useState, useCallback } from "react";
import Iframe from "../../../components/Iframe";
import Breadcrumbs from "../../../components/Breadcrumbs";
import {
  handleInputChangeWithScroll,
  handleInstructorAdd,
  MESSAGE_TYPES,
} from "../../../utils/editUtils";
import MediaSelector from "../../../components/website/MediaSelector";
import MediaGallery from "../../../components/website/MediaGallery";
import { MediaProvider, useMedia } from "../../../contexts/MediaContext";
import {
  executeValidationWithArrays,
  handleArrayInputChangeWithValidation,
  handleResetWithValidation,
} from "../../../utils/formValidation";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ResizableEditArea from "../../../components/common/ResizableEditArea";

// パンくず
const breadcrumbs = [
  { title: "Home", path: `/` },
  { title: "Instructor", path: `/instructor` },
];

// ソート可能なアイテムコンポーネント
function SortableInstructorItem({
  instructor,
  index,
  selectedInstructorIndex,
  onSelect,
  onDelete,
  onAdd,
  onInputChange,
  onTopImagesChange,
  onImagesChange,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: instructor.id || `instructor-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <section ref={setNodeRef} style={style}>
      <div
        className={`item ${
          selectedInstructorIndex === index ? "selected" : ""
        }`}
        onClick={() => onSelect(index)}
      >
        <div className="ctrl">
          <div
            className="drag-handle"
            {...attributes}
            {...listeners}
            style={{
              cursor: isDragging ? "grabbing" : "grab",
            }}
          >
            <p>{index + 1}</p>
          </div>
          <div className="icon-wrap">
            <i
              className="icon delete"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(index, instructor);
              }}
            ></i>
            <i
              className="icon add"
              onClick={(e) => {
                e.stopPropagation();
                onAdd(index);
              }}
            ></i>
          </div>
        </div>
        <div className="forms" onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            name={`instructor${index}_dancer_name`}
            value={instructor.dancer_name || ""}
            placeholder="インストラクター名を入力"
            onChange={onInputChange}
            onFocus={onInputChange}
            data-label="インストラクター名"
            data-required="true"
            data-index={index}
          />

          <input
            type="text"
            name="instagram"
            value={instructor.instagram || ""}
            placeholder="インスタグラムのユーザー名を入力（@なし）"
            onChange={onInputChange}
            onFocus={onInputChange}
            data-label="インスタグラム"
            data-index={index}
          />

          <textarea
            className="input2"
            name="profile"
            value={instructor.profile || ""}
            placeholder="プロフィールを入力"
            onChange={onInputChange}
            onFocus={onInputChange}
            data-label="プロフィール"
            data-index={index}
          />

          {/* トップ画像（複数選択） */}
          <div className="img-form h5-contents">
            <h5>Background</h5>
            <MediaSelector
              label="トップ画像"
              aspectRatio="16:9"
              type="image"
              fieldName={`instructor_${index}_top_images`}
              selectedMedia={instructor.top_images_data || []}
              onSelect={(images) => onTopImagesChange(index, images)}
              mode="multiple"
            />
          </div>

          {/* ビジュアル画像（複数選択） */}
          <div className="img-form h5-contents">
            <h5>Visual</h5>
            <MediaSelector
              label="ビジュアル画像"
              aspectRatio="16:9"
              type="image"
              fieldName={`instructor_${index}_images`}
              selectedMedia={instructor.images_data || []}
              onSelect={(images) => onImagesChange(index, images)}
              mode="multiple"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function InstructorEditPageContent() {
  const contentKey = "instructorContent"; // ページのキー
  const [message, setMessage] = useState(""); // メッセージ
  const [loadingText, setLoadingText] = useState("");
  const { isMediaOpen, fetchMediaFiles, selectMedia } = useMedia();

  // ドラッグ&ドロップのセンサー
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 配列として初期化
  const [pageContent, setPageContent] = useState({
    instructors: [],
  });

  // 変更があったかどうかを追跡
  const [hasChanges, setHasChanges] = useState(false);
  // 選択中のインストラクター
  const [selectedInstructorIndex, setSelectedInstructorIndex] = useState(null);

  // messageが文字列の場合、3秒後に自動で消去
  useEffect(() => {
    if (message && message.trim() !== "") {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [message]);

  // ファイルアップロードハンドラー
  const handleDrop = useCallback(
    async (e) => {
      console.log("🚀 handleDrop開始", e);

      if (e.preventDefault) {
        e.preventDefault();
      }

      setLoadingText("アップロード中");

      const file = e.target?.files?.[0] || e.dataTransfer?.files?.[0];
      console.log("📁 選択されたファイル:", file);

      if (!file) {
        console.log("❌ ファイルが選択されていません");
        setLoadingText("");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("images", file);
        const apiUrl = process.env.REACT_APP_API_URL;
        console.log("📤 アップロード開始:", apiUrl);

        const response = await fetch(`${apiUrl}/api/image_upload/`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        const data = await response.json();
        console.log("📥 アップロード結果:", data);

        if (!response.ok) throw new Error(data.message);

        // アップロード完了後に選択状態にする
        if (data.images?.[0]) {
          console.log("🎯 アップロード画像を選択に追加:", data.images[0]);

          // APIレスポンスをMediaGallery形式に変換
          const uploadedImage = {
            id: data.images[0].id,
            image_url: data.images[0].url || data.images[0].image_url,
            mime_type: data.images[0].mime_type,
            width: data.images[0].width,
            height: data.images[0].height,
            file_size: data.images[0].file_size,
          };
          console.log("🔄 データ変換後:", uploadedImage);

          // メディアファイル一覧を再取得
          console.log("🔄 メディアファイル一覧を再取得中...");
          await fetchMediaFiles();
          console.log("✅ メディアファイル一覧の再取得完了");

          // fetchMediaFiles完了後に選択状態にする
          // selectMedia関数を使用して統一的に選択処理を行う
          // 複数選択モードでは既存選択に追加、単一選択モードでは置き換え
          // forceAdd = trueでアップロード時の特別処理を指定
          selectMedia(uploadedImage, true);
          console.log("✅ アップロード画像の選択完了");
        } else {
          console.log("❌ data.images[0]が存在しない");
        }
      } catch (error) {
        console.error("❌ アップロードエラー:", error);
      }
      setLoadingText("");
      console.log("🏁 handleDrop終了");
    },
    [fetchMediaFiles, selectMedia, setLoadingText]
  );

  // インストラクター一覧取得
  const fetchInstructorsData = useCallback(async () => {
    setLoadingText("読み込み中");

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_instructors/`, { credentials: "include" });
      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();

      // 画像データを適切な形式に変換
      const processedData = data.map((instructor) => {
        // top_imagesをMediaSelector用のデータ形式に変換
        const top_images_data = instructor.top_images
          ? instructor.top_images
              .map((img) => {
                // APIは常にオブジェクト形式で返す
                if (
                  typeof img === "object" &&
                  img &&
                  (img.url || img.image_url)
                ) {
                  const imageUrl = img.url || img.image_url;
                  if (!imageUrl || imageUrl.trim() === "") {
                    return null; // 空のURLは除外
                  }
                  return {
                    id: img.id,
                    image_url: imageUrl,
                    mime_type: img.mime_type || "image/jpeg",
                    width: img.width || 500,
                    height: img.height || 500,
                    file_size: img.file_size,
                    favorite_flg: img.favorite_flg,
                    thumbnail_url: img.thumbnail_url,
                  };
                }
                return null; // 無効なデータの場合はnullを返す
              })
              .filter(Boolean) // nullや未定義を除外
          : [];

        // imagesをMediaSelector用のデータ形式に変換
        const images_data = instructor.images
          ? instructor.images
              .map((img) => {
                const imageUrl = img.image_url || img.url;
                if (!imageUrl || imageUrl.trim() === "") {
                  return null; // 空の画像URLの場合はnullを返す
                }
                return {
                  id: img.id,
                  image_url: imageUrl,
                  mime_type: img.mime_type || "image/jpeg",
                  width: img.width || 500,
                  height: img.height || 500,
                  file_size: img.file_size,
                  favorite_flg: img.favorite_flg,
                  thumbnail_url: img.thumbnail_url,
                };
              })
              .filter(Boolean) // nullや未定義を除外
          : [];

        const processedInstructor = {
          ...instructor,
          top_images_data,
          images_data,
          // rudo_website用にフォーマット
          top_images:
            instructor.top_images?.map((img) => img.url || img.image_url) || [],
          images:
            instructor.images?.map((img) => ({
              id: img.id,
              image_url: img.image_url || img.url,
            })) || [],
        };

        return processedInstructor;
      });

      // 取得したデータをinstructors配列に設定
      setPageContent({ instructors: processedData });
      setHasChanges(false);

      return processedData;
    } catch (error) {
      console.error("データの取得中にエラーが発生しました:", error);
      return [];
    } finally {
      setLoadingText("");
    }
  }, []);

  // 保存用のデータ取得関数（選択状態を返すため）
  const fetchInstructorsDataForSubmit = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_instructors/`, { credentials: "include" });
      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();

      // 画像データを適切な形式に変換
      const processedData = data.map((instructor) => {
        // top_imagesをMediaSelector用のデータ形式に変換
        const top_images_data = instructor.top_images
          ? instructor.top_images
              .map((img) => {
                // APIは常にオブジェクト形式で返す
                if (
                  typeof img === "object" &&
                  img &&
                  (img.url || img.image_url)
                ) {
                  const imageUrl = img.url || img.image_url;
                  if (!imageUrl || imageUrl.trim() === "") {
                    return null; // 空のURLは除外
                  }
                  return {
                    id: img.id,
                    image_url: imageUrl,
                    mime_type: img.mime_type || "image/jpeg",
                    width: img.width || 500,
                    height: img.height || 500,
                    file_size: img.file_size,
                    favorite_flg: img.favorite_flg,
                    thumbnail_url: img.thumbnail_url,
                  };
                }
                return null; // 無効なデータの場合はnullを返す
              })
              .filter(Boolean) // nullや未定義を除外
          : [];

        // imagesをMediaSelector用のデータ形式に変換
        const images_data = instructor.images
          ? instructor.images.map((img) => ({
              id: img.id,
              image_url: img.image_url || img.url,
              mime_type: "image/jpeg",
              width: 500,
              height: 500,
              thumbnail_url: img.thumbnail_url,
            }))
          : [];

        return {
          ...instructor,
          top_images_data,
          images_data,
          // rudo_website用にフォーマット
          top_images:
            instructor.top_images?.map((img) => img.url || img.image_url) || [],
          images:
            instructor.images?.map((img) => ({
              id: img.id,
              image_url: img.image_url || img.url,
            })) || [],
        };
      });

      // 取得したデータをinstructors配列に設定
      setPageContent({ instructors: processedData });
      setHasChanges(false);

      return processedData;
    } catch (error) {
      console.error("データの取得中にエラーが発生しました:", error);
      return [];
    }
  }, []);

  useEffect(() => {
    fetchInstructorsData();
    fetchMediaFiles();
  }, [fetchInstructorsData, fetchMediaFiles]);

  // インストラクター選択関数をグローバルに公開（MediaSelector用）
  useEffect(() => {
    window.selectInstructor = (index) => {
      setSelectedInstructorIndex(index);

      // iframe側にもインストラクター選択メッセージを送信
      const iframe = document.querySelector("iframe");
      if (
        iframe &&
        iframe.contentWindow &&
        pageContent.instructors &&
        pageContent.instructors[index]
      ) {
        const instructor = pageContent.instructors[index];
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.SELECT_INSTRUCTOR,
            contentKey,
            index: parseInt(index),
            instructor: instructor,
            preserveStudios: true,
            eventType: "media_select", // MediaSelector用の明確なeventType
          },
          process.env.REACT_APP_ORIGIN
        );
      }
    };

    return () => {
      delete window.selectInstructor;
    };
  }, [pageContent, contentKey]);

  // テキスト入力ハンドラー
  const handleInputChange = (e) => {
    const index = parseInt(e.target.dataset.index);
    const name = e.target.name;

    // name属性から実際のフィールド名を抽出（instructor0_dancer_name -> dancer_name）
    const fieldName = name.startsWith("instructor")
      ? name.split("_").slice(1).join("_")
      : name;

    // 入力フィールドにフォーカスした際に、そのインストラクターを選択状態にする
    if (index !== undefined && index !== selectedInstructorIndex) {
      setSelectedInstructorIndex(index);
    }

    // バリデーションエラークリア処理
    handleArrayInputChangeWithValidation(
      e,
      index,
      fieldName,
      setPageContent,
      "instructors",
      (newData) => {
        // iframe通信処理
        handleInputChangeWithScroll(
          e,
          {
            contentKey,
            currentData: newData,
            setData: () => {}, // setPageContentはhandleArrayInputChangeWithValidationで既に実行済み
          },
          fieldName
        ); // fieldNameを第3引数として渡す
      }
    );

    setHasChanges(true);
  };

  // 削除ボタンのハンドラー
  const handleDelete = async (index, instructor) => {
    // 新しく追加されたインストラクター（負のID）の場合は確認なしで即座に削除
    if (instructor.id && instructor.id < 0) {
      // UIから削除
      setPageContent((prev) => {
        const newInstructors = [...prev.instructors];
        newInstructors.splice(index, 1);

        // iframe側に削除メッセージを送信
        setTimeout(() => {
          const iframe = document.querySelector("iframe");
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.TEXT_UPDATE,
                contentKey,
                instructorContent: { instructors: newInstructors },
                isDelete: true, // 削除フラグを追加
              },
              process.env.REACT_APP_ORIGIN
            );
          }
        }, 50);
        return { ...prev, instructors: newInstructors };
      });

      setHasChanges(true);

      // 選択中のインストラクターが削除された場合、選択をクリア
      if (selectedInstructorIndex === index) {
        setSelectedInstructorIndex(null);
      } else if (selectedInstructorIndex > index) {
        // 削除されたインデックスより後ろの場合、インデックスを1つ減らす
        setSelectedInstructorIndex(selectedInstructorIndex - 1);
      }

      // 削除後に適切なインストラクターを選択
      setTimeout(() => {
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow) {
          setPageContent((currentContent) => {
            if (currentContent.instructors.length > 0) {
              // 削除後に残っているインストラクターの中から適切なものを選択
              let newSelectedIndex = 0;
              if (selectedInstructorIndex !== null) {
                if (
                  selectedInstructorIndex >= currentContent.instructors.length
                ) {
                  newSelectedIndex = currentContent.instructors.length - 1;
                } else if (selectedInstructorIndex > index) {
                  newSelectedIndex = selectedInstructorIndex - 1;
                } else if (selectedInstructorIndex === index) {
                  newSelectedIndex = Math.min(
                    index,
                    currentContent.instructors.length - 1
                  );
                } else {
                  newSelectedIndex = selectedInstructorIndex;
                }
              }

              setSelectedInstructorIndex(newSelectedIndex);

              // 新しく選択されたインストラクターをiframeに通知
              iframe.contentWindow.postMessage(
                {
                  type: MESSAGE_TYPES.SELECT_INSTRUCTOR,
                  contentKey,
                  index: newSelectedIndex,
                  instructor: currentContent.instructors[newSelectedIndex],
                  preserveStudios: true,
                  eventType: "delete_select",
                  tabValue: "",
                },
                process.env.REACT_APP_ORIGIN
              );
            }
            return currentContent;
          });
        }
      }, 200);
      return;
    }

    // 既存のインストラクターの場合は常に確認ダイアログを表示
    let confirmMessage = `${instructor.dancer_name}を本当に削除しますか？`;

    // レッスンが紐づいている場合は詳細メッセージを追加
    if (instructor.has_lessons) {
      const lessonList = instructor.lessons
        .map(
          (lesson) =>
            `・${lesson.studio_name}(${lesson.day_of_week}) ${lesson.start_time}-${lesson.end_time} ${lesson.lesson_name}`
        )
        .join("\n");

      confirmMessage =
        `${instructor.dancer_name}には${instructor.lesson_count}件のレッスンが登録されています。\n` +
        `削除すると、以下のレッスンもすべて削除されます：\n\n` +
        `${lessonList}\n\n` +
        `本当に削除しますか？`;
    }

    const confirmDelete = window.confirm(confirmMessage);

    if (!confirmDelete) {
      return; // キャンセルされた場合は何もしない
    }

    // delete_instructor_view APIを呼び出し
    try {
      setLoadingText("削除中");
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/delete_instructor/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          instructor_id: instructor.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "削除に失敗しました");
      }

      const data = await response.json();

      // UIから削除
      setPageContent((prev) => {
        const newInstructors = [...prev.instructors];
        newInstructors.splice(index, 1);

        // iframe側に削除メッセージを送信
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow) {
          setTimeout(() => {
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.DELETE_INSTRUCTOR,
                contentKey,
                instructorId: instructor.id,
              },
              process.env.REACT_APP_ORIGIN
            );

            // さらに全体のデータも更新
            setTimeout(() => {
              iframe.contentWindow.postMessage(
                {
                  type: MESSAGE_TYPES.TEXT_UPDATE,
                  contentKey,
                  instructorContent: { instructors: newInstructors },
                },
                process.env.REACT_APP_ORIGIN
              );
            }, 100);
          }, 50);
        }

        return { ...prev, instructors: newInstructors };
      });

      setHasChanges(true);

      // 選択中のインストラクターが削除された場合、選択をクリア
      if (selectedInstructorIndex === index) {
        setSelectedInstructorIndex(null);
      } else if (selectedInstructorIndex > index) {
        // 削除されたインデックスより後ろの場合、インデックスを1つ減らす
        setSelectedInstructorIndex(selectedInstructorIndex - 1);
      }

      // 削除後に適切なインストラクターを選択
      setTimeout(() => {
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow) {
          setPageContent((currentContent) => {
            if (currentContent.instructors.length > 0) {
              // 削除後に残っているインストラクターの中から適切なものを選択
              let newSelectedIndex = 0;
              if (selectedInstructorIndex !== null) {
                if (
                  selectedInstructorIndex >= currentContent.instructors.length
                ) {
                  newSelectedIndex = currentContent.instructors.length - 1;
                } else if (selectedInstructorIndex > index) {
                  newSelectedIndex = selectedInstructorIndex - 1;
                } else if (selectedInstructorIndex === index) {
                  newSelectedIndex = Math.min(
                    index,
                    currentContent.instructors.length - 1
                  );
                } else {
                  newSelectedIndex = selectedInstructorIndex;
                }
              }

              setSelectedInstructorIndex(newSelectedIndex);

              // 新しく選択されたインストラクターをiframeに通知
              iframe.contentWindow.postMessage(
                {
                  type: MESSAGE_TYPES.SELECT_INSTRUCTOR,
                  contentKey,
                  index: newSelectedIndex,
                  instructor: currentContent.instructors[newSelectedIndex],
                  preserveStudios: true,
                  eventType: "delete_select",
                  tabValue: "",
                },
                process.env.REACT_APP_ORIGIN
              );
            }
            return currentContent;
          });
        }
      }, 200);
      setMessage(data.message || "インストラクターを削除しました");
    } catch (error) {
      console.error("削除中にエラーが発生しました:", error);
      setMessage(`削除エラー: ${error.message}`);
    } finally {
      setLoadingText("");
    }
  };

  // 追加ボタンのハンドラー
  const handleAdd = (index) => {
    // 既存のインストラクターの構造を参考に、完全なオブジェクトを作成
    const newInstructor = {
      id: -Date.now(), // 一時的な負のIDを生成
      dancer_name: "",
      profile: null,
      instagram: null,
      top_images: [],
      top_images_data: [],
      movies: [],
      images: [],
      images_data: [],
      studios: [],
      is_active: 1,
      has_lessons: false,
      lesson_count: 0,
      lessons: [],
    };

    // UIを更新
    setPageContent((prev) => {
      const newInstructors = [...prev.instructors];
      newInstructors.splice(index + 1, 0, newInstructor);
      const updatedContent = { ...prev, instructors: newInstructors };

      // 更新後のデータを使用してiframeにメッセージを送信
      setTimeout(() => {
        handleInstructorAdd(newInstructor, index + 1, {
          contentKey,
          currentData: updatedContent,
        });
      }, 0);

      return updatedContent;
    });

    setHasChanges(true);

    // 追加したインストラクターを選択
    setSelectedInstructorIndex(index + 1);
  };

  // インストラクターを選択（クリック時）
  const handleInstructorSelect = (index) => {
    setSelectedInstructorIndex(index);

    // iframe側にもインストラクター選択メッセージを送信
    const iframe = document.querySelector("iframe");
    if (
      iframe &&
      iframe.contentWindow &&
      pageContent.instructors &&
      pageContent.instructors[index]
    ) {
      const instructor = pageContent.instructors[index];
      iframe.contentWindow.postMessage(
        {
          type: MESSAGE_TYPES.SELECT_INSTRUCTOR,
          contentKey,
          index: parseInt(index),
          instructor: instructor,
          preserveStudios: true,
          target: ".edit_dancer_name",
          eventType: "click", // クリック用の明確なeventType
          tabValue: "", // クリック時はタブを閉じる
        },
        process.env.REACT_APP_ORIGIN
      );
    }
  };

  // ドラッグ終了時のハンドラー
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setPageContent((prev) => {
        const oldIndex = prev.instructors.findIndex(
          (item) =>
            (item.id || `instructor-${prev.instructors.indexOf(item)}`) ===
            active.id
        );
        const newIndex = prev.instructors.findIndex(
          (item) =>
            (item.id || `instructor-${prev.instructors.indexOf(item)}`) ===
            over.id
        );

        const newInstructors = arrayMove(prev.instructors, oldIndex, newIndex);

        // 選択中のインストラクターのインデックスを更新
        if (selectedInstructorIndex === oldIndex) {
          setSelectedInstructorIndex(newIndex);
        } else if (
          selectedInstructorIndex > oldIndex &&
          selectedInstructorIndex <= newIndex
        ) {
          setSelectedInstructorIndex(selectedInstructorIndex - 1);
        } else if (
          selectedInstructorIndex < oldIndex &&
          selectedInstructorIndex >= newIndex
        ) {
          setSelectedInstructorIndex(selectedInstructorIndex + 1);
        }

        // iframe側に並び順変更を通知
        setTimeout(() => {
          const iframe = document.querySelector("iframe");
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.TEXT_UPDATE,
                contentKey,
                instructorContent: { instructors: newInstructors },
                isReorder: true,
              },
              process.env.REACT_APP_ORIGIN
            );
          }
        }, 50);

        setHasChanges(true);
        return { ...prev, instructors: newInstructors };
      });
    }
  };

  // トップ画像変更ハンドラー
  const handleTopImagesChange = useCallback(
    (index, images) => {
      // 選択状態を更新
      setSelectedInstructorIndex(index);

      // 値を更新
      setPageContent((prev) => {
        const newInstructors = [...prev.instructors];
        newInstructors[index] = {
          ...newInstructors[index],
          top_images_data: images,
          // URL文字列配列に更新（rudo_website用）
          top_images: images.map((img) => img.image_url),
        };

        // iframeに通知（更新後のデータを使用）
        setTimeout(() => {
          const iframe = document.querySelector("iframe");
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.ARRAY_ITEM_UPDATE,
                contentKey,
                fieldName: "top_images",
                value: images.map((img) => img.image_url), // URL文字列配列
                index,
                instructor: {
                  ...newInstructors[index], // 更新後のデータを使用
                  top_images: images.map((img) => img.image_url), // URL文字列配列
                },
                resetImages: true, // スライドをリセットするフラグ
              },
              process.env.REACT_APP_ORIGIN
            );
          }
        }, 100);

        return { ...prev, instructors: newInstructors };
      });

      setHasChanges(true);
    },
    [contentKey, selectedInstructorIndex]
  );

  // イメージ変更ハンドラー
  const handleImagesChange = useCallback(
    (index, images) => {
      // 選択状態を更新
      setSelectedInstructorIndex(index);

      // 値を更新
      setPageContent((prev) => {
        const newInstructors = [...prev.instructors];
        newInstructors[index] = {
          ...newInstructors[index],
          images_data: images,
          // オブジェクトの配列も更新（rudo_website用: {id, image_url}形式）
          images: images.map((img) => ({
            id: img.id,
            image_url: img.image_url,
          })),
        };

        // iframeに通知（更新後のデータを使用）
        setTimeout(() => {
          const iframe = document.querySelector("iframe");
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.ARRAY_ITEM_UPDATE,
                contentKey,
                fieldName: "images",
                value: images.map((img) => ({
                  id: img.id,
                  image_url: img.image_url,
                })),
                index,
                instructor: {
                  ...newInstructors[index], // 更新後のデータを使用
                  images: images.map((img) => ({
                    id: img.id,
                    image_url: img.image_url,
                  })),
                },
                tabValue: 5, // VISUALタブを開く
              },
              process.env.REACT_APP_ORIGIN
            );
          }
        }, 100);

        return { ...prev, instructors: newInstructors };
      });

      setHasChanges(true);
    },
    [contentKey, selectedInstructorIndex]
  );

  // バリデーションルールを定義
  const validationRules = {};

  // 配列要素のバリデーションルール
  const arrayValidationRules = {
    instructors: {
      dancer_name: { required: true },
    },
  };

  // フィールドラベルのマッピング
  const fieldLabels = {
    dancer_name: "インストラクター名",
  };

  // 送信処理
  const handleSubmit = async () => {
    // バリデーション実行
    if (
      !executeValidationWithArrays(
        pageContent,
        validationRules,
        arrayValidationRules,
        fieldLabels,
        setMessage
      )
    ) {
      return;
    }

    setLoadingText("保存中");

    try {
      const apiUrl = process.env.REACT_APP_API_URL;

      // 更新データの準備
      const updateData = {
        instructors: pageContent.instructors.map((instructor, index) => ({
          ...instructor,
          // 画像IDの配列に変換
          top_image_ids: instructor.top_images_data
            ? instructor.top_images_data
                .map((img) => img.id)
                .filter((id) => id !== null)
            : [],
          image_ids: instructor.images_data
            ? instructor.images_data
                .map((img) => img.id)
                .filter((id) => id !== null)
            : [],
          // 並び順を追加
          sort_order: index + 1,
        })),
      };

      const response = await fetch(`${apiUrl}/api/update_instructors/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "データの更新に失敗しました");
      }

      const data = await response.json();

      // 現在選択されているインストラクターの情報を保存
      const currentSelectedInstructor =
        selectedInstructorIndex !== null
          ? pageContent.instructors[selectedInstructorIndex]
          : null;

      // 最新データを再取得
      const newData = await fetchInstructorsDataForSubmit();

      // 保存後に選択状態を復元
      if (currentSelectedInstructor) {
        // dancer_nameまたはIDで一致するインストラクターを探す
        let newSelectedIndex = -1;

        // まずIDで検索（既存のインストラクターの場合）
        if (currentSelectedInstructor.id && currentSelectedInstructor.id > 0) {
          newSelectedIndex = newData.findIndex(
            (instructor) => instructor.id === currentSelectedInstructor.id
          );
        }

        // IDで見つからない場合はdancer_nameで検索（新規作成の場合）
        if (newSelectedIndex === -1) {
          newSelectedIndex = newData.findIndex(
            (instructor) =>
              instructor.dancer_name === currentSelectedInstructor.dancer_name
          );
        }

        // 見つからない場合は最初のインストラクターを選択
        if (newSelectedIndex === -1 && newData.length > 0) {
          newSelectedIndex = 0;
        }

        console.log("Restoring selection to index:", newSelectedIndex);
        setSelectedInstructorIndex(newSelectedIndex);

        // iframe側にも新しいデータと選択状態を送信
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow && newSelectedIndex >= 0) {
          setTimeout(() => {
            // まず全体データを更新
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.TEXT_UPDATE,
                contentKey,
                instructorContent: { instructors: newData },
                isSubmit: true, // 保存後フラグを追加
              },
              process.env.REACT_APP_ORIGIN
            );

            // その後、選択状態を復元
            setTimeout(() => {
              iframe.contentWindow.postMessage(
                {
                  type: MESSAGE_TYPES.SELECT_INSTRUCTOR,
                  contentKey,
                  index: newSelectedIndex,
                  instructor: newData[newSelectedIndex],
                  preserveStudios: true,
                  eventType: "submit_restore",
                  tabValue: "",
                  forceUpdate: true,
                },
                process.env.REACT_APP_ORIGIN
              );
            }, 200);
          }, 100);
        }
      }
      setMessage(data.message || "インストラクター情報を更新しました");
    } catch (error) {
      console.error("更新中にエラーが発生しました:", error);
      setMessage(`エラー: ${error.message}`);
    }

    setLoadingText("");
  };

  // リセット処理専用の関数
  const handleReset = useCallback(async () => {
    handleResetWithValidation(async () => {
      // 現在選択されているインストラクターのインデックスを保存
      const currentSelectedIndex = selectedInstructorIndex;

      // データを再取得してpageContentを更新
      setLoadingText("リセット中");

      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/api/get_instructors/`, { credentials: "include" });
        if (!response.ok) {
          throw new Error("ネットワークレスポンスが異常です。");
        }
        const data = await response.json();

        // 画像データを適切な形式に変換
        const processedData = data.map((instructor) => {
          // top_imagesをMediaSelector用のデータ形式に変換
          const top_images_data = instructor.top_images
            ? instructor.top_images
                .map((img) => {
                  // APIは常にオブジェクト形式で返す
                  if (
                    typeof img === "object" &&
                    img &&
                    (img.url || img.image_url)
                  ) {
                    const imageUrl = img.url || img.image_url;
                    if (!imageUrl || imageUrl.trim() === "") {
                      return null; // 空のURLは除外
                    }
                    return {
                      id: img.id,
                      image_url: imageUrl,
                      mime_type: img.mime_type || "image/jpeg",
                      width: img.width || 500,
                      height: img.height || 500,
                      file_size: img.file_size,
                      favorite_flg: img.favorite_flg,
                    };
                  }
                  return null; // 無効なデータの場合はnullを返す
                })
                .filter(Boolean) // nullや未定義を除外
            : [];

          // imagesをMediaSelector用のデータ形式に変換
          const images_data = instructor.images
            ? instructor.images.map((img) => ({
                id: img.id,
                image_url: img.image_url || img.url,
                mime_type: "image/jpeg",
                width: 500,
                height: 500,
              }))
            : [];

          return {
            ...instructor,
            top_images_data,
            images_data,
            // rudo_website用にフォーマット
            top_images:
              instructor.top_images?.map((img) => img.url || img.image_url) ||
              [],
            images:
              instructor.images?.map((img) => ({
                id: img.id,
                image_url: img.image_url || img.url,
              })) || [],
          };
        });

        // 取得したデータをinstructors配列に設定
        setPageContent({ instructors: processedData });
        setHasChanges(false);

        // 選択されていたインストラクターのインデックスを決定
        let targetIndex = 0; // デフォルトは1人目
        let targetInstructor = processedData[0];

        if (
          currentSelectedIndex !== null &&
          currentSelectedIndex >= 0 &&
          currentSelectedIndex < processedData.length
        ) {
          // 元の選択インデックスが有効な場合はそれを使用
          targetIndex = currentSelectedIndex;
          targetInstructor = processedData[currentSelectedIndex];
        }

        // 選択状態を元のインデックスに戻す
        setSelectedInstructorIndex(targetIndex);

        // iframe側にリセットメッセージを送信
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow && processedData.length > 0) {
          // まずリセットフラグでデータを更新
          setTimeout(() => {
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.TEXT_UPDATE,
                contentKey,
                instructorContent: { instructors: processedData },
                isReset: true,
              },
              process.env.REACT_APP_ORIGIN
            );

            // その後、元の選択されていたインストラクターを選択
            setTimeout(() => {
              iframe.contentWindow.postMessage(
                {
                  type: MESSAGE_TYPES.SELECT_INSTRUCTOR,
                  contentKey,
                  index: targetIndex,
                  instructor: targetInstructor,
                  preserveStudios: false, // studiosを保持しない
                  eventType: "reset",
                  tabValue: "",
                  forceUpdate: true,
                },
                process.env.REACT_APP_ORIGIN
              );
            }, 500);
          }, 200);
        }
      } catch (error) {
        console.error("リセット中にエラーが発生しました:", error);
      }

      setLoadingText("");
    });
  }, [contentKey, selectedInstructorIndex, pageContent.instructors]);

  // スタイル要素を作成
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = `
      .forms {
        grid-template-columns: auto !important;
      }
    `;

    // head に追加
    document.head.appendChild(styleElement);

    // クリーンアップ関数
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            {/* パンくず */}
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
            />

            <div className={`message ${message ? "success" : ""}`}>
              <p>{message}</p>
            </div>

            <div className="form">
              <div className="area2">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={pageContent.instructors.map(
                      (instructor, index) =>
                        instructor.id || `instructor-${index}`
                    )}
                    strategy={verticalListSortingStrategy}
                  >
                    {pageContent.instructors &&
                      pageContent.instructors.map((instructor, index) => (
                        <SortableInstructorItem
                          key={instructor.id || `instructor-${index}`}
                          instructor={instructor}
                          index={index}
                          selectedInstructorIndex={selectedInstructorIndex}
                          onSelect={handleInstructorSelect}
                          onDelete={handleDelete}
                          onAdd={handleAdd}
                          onInputChange={handleInputChange}
                          onTopImagesChange={handleTopImagesChange}
                          onImagesChange={handleImagesChange}
                        />
                      ))}
                  </SortableContext>
                </DndContext>
              </div>
            </div>

            {/* 送信ボタン */}
            <div className="submit-area">
              <button type="reset" onClick={handleReset} disabled={!hasChanges}>
                Reset
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={!hasChanges}
              >
                Submit
              </button>
            </div>

            {/* メディア / オーバーレイ */}
            {isMediaOpen && <MediaGallery onUpload={handleDrop} />}
          </>
        )}
      </ResizableEditArea>

      {/* ローディング */}
      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}

      {/* プレビュー */}
      <Iframe
        path={
          pageContent.instructors?.length > 0 && pageContent.instructors[0]?.id
            ? `/instructor?instructor=${pageContent.instructors[0].id}`
            : "/instructor"
        }
        data={pageContent}
        contentKey={contentKey}
      />
    </>
  );
}

// MediaProviderでラップしたコンポーネントをエクスポート
export default function InstructorEditPage() {
  return (
    <MediaProvider>
      <InstructorEditPageContent />
    </MediaProvider>
  );
}
